/**
 * Training Artifact Quiz — grading rule (Phase Q1).
 *
 * The canonical pass rule lives here and is MIRRORED by the server-side SQL RPC
 * (submit_training_quiz_attempt) which is the authoritative grader — the client
 * never sees correct answers. This module documents/tests the rule and builds the
 * honest `metadata.trainingQuiz` payload.
 */
import type {
  QuizAnswers,
  QuizAuthoringQuestion,
  QuizQuestionResult,
  TrainingQuizMetadata,
} from './quizTypes';

/** Pass threshold — 70% (approved Q1 decision). */
export const QUIZ_PASS_THRESHOLD_PCT = 70;

export const QUIZ_MIN_QUESTIONS = 3;
export const QUIZ_MAX_QUESTIONS = 5;
/** Retake cap — limited attempts to blunt 4-choice brute-forcing. */
export const QUIZ_MAX_ATTEMPTS = 3;

export interface QuizScore {
  totalQuestions: number;
  correctCount: number;
  scorePct: number;
  passed: boolean;
}

/**
 * Score an attempt against the authoritative answer key. `scorePct` is
 * floor(correct/total*100); pass when scorePct >= 70.
 * (5 questions: 4/5=80 passes, 3/5=60 fails. 3 questions: 3/3=100 passes,
 * 2/3=66 fails — below 70, as intended.)
 */
export function scoreQuiz(answers: QuizAnswers, correctIndexes: number[]): QuizScore {
  const totalQuestions = correctIndexes.length;
  let correctCount = 0;
  for (let i = 0; i < totalQuestions; i += 1) {
    if (answers[i] === correctIndexes[i]) correctCount += 1;
  }
  const scorePct =
    totalQuestions > 0 ? Math.floor((correctCount / totalQuestions) * 100) : 0;
  return {
    totalQuestions,
    correctCount,
    scorePct,
    passed: scorePct >= QUIZ_PASS_THRESHOLD_PCT,
  };
}

export function isPassingScore(scorePct: number): boolean {
  return scorePct >= QUIZ_PASS_THRESHOLD_PCT;
}

/** Per-question results (returned ONLY after server grading). */
export function buildQuestionResults(
  questions: QuizAuthoringQuestion[],
  answers: QuizAnswers,
): QuizQuestionResult[] {
  return questions.map((q, i) => ({
    question: q.question,
    chosenIndex: answers[i] ?? -1,
    correctIndex: q.correct_index,
    correct: answers[i] === q.correct_index,
    explanation: q.explanation,
  }));
}

/**
 * The honest metadata signal. INTEGRITY: companion_verified / provider_verified /
 * completed_watch_verified are ALWAYS false; a pass sets quiz_confidence_boost
 * (a DISPLAY-only boost) but callers must NOT change completion_confidence.
 */
export function buildTrainingQuizMetadata(args: {
  quizVersion: number;
  attemptId: string | null;
  scorePct: number;
  passed: boolean;
  at: string;
}): TrainingQuizMetadata {
  const base: TrainingQuizMetadata = {
    source: 'knowledge_check',
    quiz_version: args.quizVersion,
    quiz_attempt_id: args.attemptId,
    quiz_score_pct: args.scorePct,
    quiz_passed: args.passed,
    quiz_confidence_boost: args.passed,
    companion_verified: false,
    provider_verified: false,
    completed_watch_verified: false,
    at: args.at,
  };
  if (!args.passed) {
    base.retake_available = true;
    base.review_recommended = true;
  }
  return base;
}
